import { useEffect, useState } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Cell,
  Legend,
} from "recharts";
import { getSymbolPerformance, type SymbolPerformance } from "../../api/client";

interface Props {
  portfolioId: number | null;
}

const fmtUSD = (v: number) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(v);

export default function PerformanceChart({ portfolioId }: Props) {
  const [data, setData] = useState<SymbolPerformance[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!portfolioId) return;
    setLoading(true);
    setError(null);
    getSymbolPerformance(portfolioId)
      .then((res) => setData(res.data))
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, [portfolioId]);

  if (!portfolioId) return <p style={{ color: "#64748b" }}>Select a portfolio.</p>;
  if (loading) return <p>Loading...</p>;
  if (error) return <p style={{ color: "red" }}>Error: {error}</p>;

  if (data.length === 0) {
    return (
      <div style={{ textAlign: "center", color: "#94a3b8", padding: "40px 0" }}>
        <p>No trade data available yet.</p>
      </div>
    );
  }

  const sorted = [...data].sort((a, b) => b.realized_pnl - a.realized_pnl);

  return (
    <div>
      <h2 style={{ margin: "0 0 16px", color: "#1e293b" }}>Per-Symbol Performance</h2>

      <ResponsiveContainer width="100%" height={380}>
        <BarChart data={sorted} margin={{ top: 5, right: 30, left: 20, bottom: 40 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
          <XAxis dataKey="symbol" tick={{ fontSize: 12 }} angle={-30} textAnchor="end" />
          <YAxis tickFormatter={fmtUSD} tick={{ fontSize: 12 }} width={80} />
          <Tooltip formatter={(v: number | undefined) => v != null ? fmtUSD(v) : "—"} />
          <Legend />
          <Bar dataKey="realized_pnl" name="Realized P&L">
            {sorted.map((entry, index) => (
              <Cell key={`cell-${index}`} fill={entry.realized_pnl >= 0 ? "#10b981" : "#ef4444"} />
            ))}
          </Bar>
          <Bar dataKey="unrealized_pnl" name="Unrealized P&L">
            {sorted.map((entry, index) => (
              <Cell key={`ucell-${index}`} fill={entry.unrealized_pnl >= 0 ? "#3b82f6" : "#f59e0b"} />
            ))}
          </Bar>
        </BarChart>
      </ResponsiveContainer>

      {/* Table */}
      <div style={{ overflowX: "auto", marginTop: 24 }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 14 }}>
          <thead>
            <tr style={{ background: "#f1f5f9" }}>
              {["Symbol", "Trades", "Total Proceeds", "Total Commission", "Realized P&L", "Unrealized P&L"].map((h) => (
                <th key={h} style={{ padding: "8px 12px", textAlign: "left", color: "#475569", fontWeight: 600 }}>
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {sorted.map((row) => (
              <tr key={row.symbol} style={{ borderBottom: "1px solid #f1f5f9" }}>
                <td style={{ padding: "8px 12px", fontWeight: 700 }}>{row.symbol}</td>
                <td style={{ padding: "8px 12px", textAlign: "right" }}>{row.trade_count}</td>
                <td style={{ padding: "8px 12px", textAlign: "right" }}>{fmtUSD(row.total_proceeds)}</td>
                <td style={{ padding: "8px 12px", textAlign: "right", color: "#dc2626" }}>{fmtUSD(row.total_commission)}</td>
                <td style={{ padding: "8px 12px", textAlign: "right", fontWeight: 600, color: row.realized_pnl >= 0 ? "#16a34a" : "#dc2626" }}>
                  {fmtUSD(row.realized_pnl)}
                </td>
                <td style={{ padding: "8px 12px", textAlign: "right", fontWeight: 600, color: (row.unrealized_pnl ?? 0) >= 0 ? "#3b82f6" : "#f59e0b" }}>
                  {fmtUSD(row.unrealized_pnl ?? 0)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
